import { MapViewer } from '@3ddv/dvm-internal';
import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DvmService } from '@features/digital-venue/services/dvm.service';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { map, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { ModalsService } from '../modals/modals.service';
import {
  GeneralAvailabilityDict,
  Seat,
  SeatAvailabilityDict,
  SeatAvailabilitySectionDict,
} from '../models';
import { PriceRange } from '../models/seat-availability.model';
import { ConfigurationService } from './configuration.service';
import { DemoDataService } from './demo-data.service';
import { DvmService as DvmServiceCore } from './dvm.service';
import { TutorialService } from './tutorial.service';
import { UnavailableSectionsStore } from './unavailable-sections.store';

@Injectable({
  providedIn: 'root',
})
export class AvailabilityService {
  private readonly failedPrefetchSections = new Subject<string[]>();
  readonly failedPrefetchSections$ = this.failedPrefetchSections.asObservable();
  readonly unavailableSectionIds = inject(UnavailableSectionsStore).ids;

  viewer: MapViewer;
  sectionsAvailability: GeneralAvailabilityDict = {};
  seatsAvailability: SeatAvailabilitySectionDict = {};
  sectionsOnlyAdas: GeneralAvailabilityDict = {};
  minSeatsLimit: number;
  maxSeatsLimit: number;
  adaSeats: Seat[] = [];
  priceRange$ = new ReplaySubject<PriceRange>(1);
  priceRange: PriceRange = { min: 100000000, max: -1 };
  public test: { min: number; max: number } = { min: 100000000, max: -1 };

  private prefetchedSeatAvailability: {
    data: SeatAvailabilityDict;
    sectionIds: string[];
    timestamp: number;
  } | null = null;
  private hasPrefetched = false;

  public isPrefetching$ = new ReplaySubject<boolean>(1);

  private dvmService = inject(DvmService);
  private dvmServiceCore = inject(DvmServiceCore);
  private connectionService = inject(ConnectionService);
  private configurationService = inject(ConfigurationService);
  private modalsService = inject(ModalsService);
  private tutorialService = inject(TutorialService);
  private demoDataService = inject(DemoDataService);

  constructor() {
    const viewerSubscription = this.dvmService.viewerSubject.subscribe({
      next: response => (this.viewer = response),
      error: error => {
        console.error(error);
        const modalData = {
          title: 'ERROR',
          content: 'An Error occurred while trying to get the Viewer.',
          acceptBtnName: 'CLOSE',
        };
        this.modalsService.openModal(modalData);
      },
    });
    this.dvmService.viewerSubscriptions.push(viewerSubscription);
  }

  private prefetchSeatAvailability(
    sectionsArray: string[],
    eventId = this.connectionService.eventId
  ) {
    this.isPrefetching$.next(true);
    this.unavailableSectionIds.clear();
    const context = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);
    this.connectionService
      .getMultiSectionAvailability(eventId, sectionsArray, context)
      .subscribe({
        next: ({ data, failedSectionIds = [] }) => {
          this.prefetchedSeatAvailability = {
            data: data as SeatAvailabilityDict,
            sectionIds: [...sectionsArray].sort(),
            timestamp: Date.now(),
          };
          this.hasPrefetched = true;

          const availability = data as SeatAvailabilityDict;
          sectionsArray.forEach(sectionId => {
            const filteredAvailability = Object.entries(availability).filter(
              ([seatId, _]) => seatId.split('-')[0] === sectionId
            );
            this.seatsAvailability[sectionId] = Object.fromEntries(
              filteredAvailability
            ) as SeatAvailabilityDict;
          });

          this.isPrefetching$.next(false);

          failedSectionIds.forEach(sectionId =>
            this.unavailableSectionIds.add(sectionId)
          );
          this.failedPrefetchSections.next(failedSectionIds);
        },
        error: () => {
          this.prefetchedSeatAvailability = null;
          this.hasPrefetched = false;
          this.isPrefetching$.next(false);
        },
      });
  }

  getSectionAvailability(eventId = this.connectionService.eventId) {
    if (this.tutorialService.isOpen()) {
      return new Observable<Array<string>>(observer => {
        this.sectionsAvailability =
          this.tutorialService.getTutorialSectionAvailability();

        observer.next(this.tutorialService.getTutorialSectionAvailabilityIds());
        observer.complete();
      });
    }

    if (this.demoDataService.isDemoActive()) {
      return new Observable<Array<string>>(observer => {
        this.sectionsAvailability =
          this.demoDataService.buildGeneralAvailabilityByNodes(
            this.dvmServiceCore.viewerService.getNodesByType('section')
          );
        this.sectionsOnlyAdas =
          this.demoDataService.buildGeneralAvailabilityByNodes(
            this.dvmServiceCore.viewerService.getNodesByType(
              'general_admission'
            )
          );
        observer.next(Object.keys(this.sectionsAvailability));
        observer.complete();
      });
    }

    if (!this.connectionService.eventId) {
      return throwError(() => ({
        error: {
          message: `The event requested is invalid. If you are being redirected from an external website, please call ${this.configurationService.configuration.phone} or email ${this.configurationService.configuration.email}.`,
          code: 'E_INVALID_EVENT_ID',
        },
      }));
    }

    return this.connectionService.getGeneralAvailability(eventId).pipe(
      map(({ data }) => {
        const result: string[] = [];
        const availability = data as GeneralAvailabilityDict;

        Object.entries(availability).forEach(([mmcId, item]) => {
          if (item.availableQuantity + item.availableQuantityAda === 0) {
            delete availability[mmcId];
          } else {
            if (item.availableQuantity === 0 && item.availableQuantityAda > 0) {
              this.sectionsOnlyAdas[mmcId] = item;
              delete availability[mmcId];
              return;
            }

            if (item.priceRange[0].value < this.priceRange.min) {
              this.priceRange.min = item.priceRange[0].value;
            }
            if (item.priceRange[1].value > this.priceRange.max) {
              this.priceRange.max = item.priceRange[1].value;
            }
            this.priceRange$.next(this.priceRange);
            result.push(mmcId);
          }
        });

        this.sectionsAvailability = availability;

        if (!this.hasPrefetched) {
          this.prefetchSeatAvailability(result, eventId);
        }

        return result;
      })
    );
  }

  getSeatAvailability(
    sectionsArray: string[],
    eventId = this.connectionService.eventId
  ) {
    if (this.tutorialService.isOpen()) {
      return new Observable<Array<string>>(observer => {
        this.seatsAvailability =
          this.tutorialService.getTutorialSeatsAvailability();

        observer.next(this.tutorialService.getTutorialSeatsAvailabilityIds());
        observer.complete();
      });
    }

    if (this.demoDataService.isDemoActive()) {
      return new Observable<Array<string>>(observer => {
        const newSectionAvailability =
          this.demoDataService.buildSeatsAvailabilityByNodes(
            sectionsArray[0],
            this.dvmServiceCore.viewerService.getNodesByParent(sectionsArray[0])
          );
        this.seatsAvailability = {
          ...this.seatsAvailability,
          ...newSectionAvailability,
        };
        const allSeatIds = Object.values(this.seatsAvailability).flatMap(
          sectionSeats => Object.keys(sectionSeats)
        );
        observer.next(allSeatIds);
        observer.complete();
      });
    }

    const cache = this.prefetchedSeatAvailability;
    const now = Date.now();
    const FIVE_MIN = 5 * 60 * 1000;
    if (
      cache &&
      now - cache.timestamp < FIVE_MIN &&
      cache.sectionIds.length === sectionsArray.length &&
      cache.sectionIds.every((id, idx) => id === [...sectionsArray].sort()[idx])
    ) {
      return new Observable<Array<string>>(observer => {
        const availability = cache.data;
        sectionsArray.forEach(sectionId => {
          const filteredAvailability = Object.entries(availability).filter(
            ([seatId, _]) => seatId.split('-')[0] === sectionId
          );
          this.seatsAvailability[sectionId] = Object.fromEntries(
            filteredAvailability
          ) as SeatAvailabilityDict;
        });
        observer.next(Object.keys(availability as SeatAvailabilityDict));
        observer.complete();
      });
    }

    return this.connectionService
      .getMultiSectionAvailability(eventId, sectionsArray)
      .pipe(
        map(({ data }) => {
          const availability = data as SeatAvailabilityDict;

          sectionsArray.forEach(sectionId => {
            const filteredAvailability = Object.entries(
              availability as SeatAvailabilityDict
            ).filter(([seatId, _]) => seatId.split('-')[0] === sectionId);
            this.seatsAvailability[sectionId] = Object.fromEntries(
              filteredAvailability
            ) as SeatAvailabilityDict;
          });

          return Object.keys(availability as SeatAvailabilityDict);
        })
      );
  }

  activeAdaSections() {
    this.dvmService.viewer.setAvailable(
      'section',
      Object.keys(this.sectionsOnlyAdas)
    );
  }

  deactivateOnlyAdaSections() {
    this.dvmService.viewer.setUnavailable(
      'section',
      Object.keys(this.sectionsOnlyAdas)
    );
  }

  getSeatByIdFromAvailability(seatId: string) {
    const sectionId = seatId.split('-')[0];
    const sectionAvailability = this.seatsAvailability[sectionId];
    return sectionAvailability?.[seatId];
  }
}
